import Phaser from "phaser";
import { AssetConfig } from "./AssetConfig";
import { BoardLogic } from "./BoardLogic";
import type { Cell, TileData } from "./types";

const ASSET_JSON_URL = "assets/photos/photos.json";
const TILE_PADDING = 4;        // pixels between tiles for the grid look
const TILE_BORDER_COLOR = 0x0f3460;
const TILE_SELECTED_COLOR = 0xffd166;

const HUD_FONT_LG = "22px Arial";

interface TileSprite {
  image: Phaser.GameObjects.Image;
  border: Phaser.GameObjects.Rectangle;
  data: TileData;
  cell: Cell;
}

/**
 * Phaser scene for the person-photo match-3 game.
 *
 * Layering:
 *   - BoardSprite (rows×cols of {image, border})
 *   - HUD: title, score, high score, instructions
 *
 * Animation flow:
 *   1. User selects tile A, then tile B (or swipes A -> B).
 *   2. Validate adjacency + simulate match.
 *   3. a) No match: animate A->B and B->A, then animate back. No state change.
 *      b) Match:  mutate BoardLogic, animate swap, then chain:
 *          match -> fall -> refill -> repeat until stable -> deadlock check.
 *   4. During all of this, input is locked.
 */
export class GameScene extends Phaser.Scene {
  private assetConfig!: AssetConfig;
  private boardLogic!: BoardLogic;

  /** Keyed by `${row},${col}` -> rendered sprite bundle for that cell. */
  private tiles: Map<string, TileSprite> = new Map();
  /** Selected tile during click-click interaction (single-select highlight). */
  private selected: Cell | null = null;
  /** Pointer-down origin cell + position for swipe interpretation. */
  private pointerOrigin: { cell: Cell; x: number; y: number } | null = null;

  private inputLocked = false;
  /** True while the player is mid-drag (so click-click doesn't double-fire). */
  private dragging = false;

  private tileSize = 80;
  private boardX = 0;
  private boardY = 0;

  private score = 0;
  private highScore = 0;
  private scoreText!: Phaser.GameObjects.Text;
  private highScoreText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private newHighScoreMark: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: "GameScene" });
  }

  // -------------------------------------------------------------------------
  // Phaser preload / create
  // -------------------------------------------------------------------------

  preload(): void {
    // All assets (photos.json + every photo image) are loaded dynamically in
    // bootstrap() so we can react to fetch errors before showing anything.
  }

  private loadingText: Phaser.GameObjects.Text | null = null;

  async create(): Promise<void> {
    this.loadingText = this.add
      .text(this.scale.width / 2, this.scale.height / 2, "Loading…", {
        fontFamily: "Arial",
        fontSize: "20px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    try {
      await this.bootstrap();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Failed to start the game:", err);
      if (this.loadingText) {
        this.loadingText.destroy();
        this.loadingText = null;
      }
      this.add
        .text(
          this.scale.width / 2,
          this.scale.height / 2,
          `Failed to load assets:\n${msg}`,
          {
            fontFamily: "Arial",
            fontSize: "16px",
            color: "#ff6b6b",
            align: "center",
            wordWrap: { width: this.scale.width - 32 },
          },
        )
        .setOrigin(0.5);
    }
  }

  private async bootstrap(): Promise<void> {
    this.assetConfig = new AssetConfig();
    await this.assetConfig.load(ASSET_JSON_URL);

    // Preload every photo texture via Phaser's loader, then wait for completion.
    const photos = this.assetConfig.getConfig().photos;
    if (photos.length > 0) {
      await new Promise<void>((resolve) => {
        let resolved = false;
        const finish = () => {
          if (resolved) return;
          resolved = true;
          resolve();
        };
        this.load.once("complete", finish);
        this.load.on("loaderror", () => finish());
        for (const photo of photos) {
          this.load.image(photo.photoId, photo.photoUrl);
        }
        this.load.start();
        // Defensive timeout in case no events fire at all.
        this.time.delayedCall(15_000, finish, [], this);
      });
    }

    this.boardLogic = new BoardLogic(this.assetConfig, {
      rows: 8,
      cols: 8,
      personsPerLevel: 5,
      minMatchLength: 3,
    });

    this.loadHighScore();
    this.layout();
    this.buildHUD();
    this.registerInputHandlers();
    if (this.loadingText) {
      this.loadingText.destroy();
      this.loadingText = null;
    }
    this.startNewGame();
  }

  // -------------------------------------------------------------------------
  // Layout / HUD
  // -------------------------------------------------------------------------

  private layout(): void {
    const { width } = this.scale;
    this.tileSize = this.tileSizeFor(width);
    this.boardY = Math.round(this.scale.height * 0.18);
    const boardPx = this.cols() * this.tileSize + (this.cols() - 1) * TILE_PADDING;
    this.boardX = Math.round((width - boardPx) / 2);
  }

  private tileSizeFor(width: number): number {
    // Leave 16px left/right gutters; the rest is divided across tiles + padding.
    const usable = width - 32;
    return Math.floor((usable - (this.cols() - 1) * TILE_PADDING) / this.cols());
  }

  private cols(): number {
    return this.boardLogic?.cols ?? 8;
  }

  private rows(): number {
    return this.boardLogic?.rows ?? 8;
  }

  private cellToWorld(cell: Cell): { x: number; y: number } {
    return {
      x:
        this.boardX +
        cell.col * (this.tileSize + TILE_PADDING) +
        this.tileSize / 2,
      y:
        this.boardY +
        cell.row * (this.tileSize + TILE_PADDING) +
        this.tileSize / 2,
    };
  }

  private worldToCell(x: number, y: number): Cell | null {
    const relX = x - this.boardX;
    const relY = y - this.boardY;
    if (relX < 0 || relY < 0) return null;
    const step = this.tileSize + TILE_PADDING;
    const col = Math.floor(relX / step);
    const row = Math.floor(relY / step);
    if (row < 0 || row >= this.rows() || col < 0 || col >= this.cols()) return null;
    // Reject cells in the padding gutter between tiles.
    const localX = relX - col * step;
    const localY = relY - row * step;
    if (localX > this.tileSize || localY > this.tileSize) return null;
    return { row, col };
  }

  private buildHUD(): void {
    const { width, height } = this.scale;
    const centerX = width / 2;

    this.add
      .text(centerX, 24, "Person Photo Match-3", {
        fontFamily: "Arial",
        fontSize: "26px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    this.scoreText = this.add
      .text(16, 58, "Score: 0", {
        fontFamily: "Arial",
        fontSize: HUD_FONT_LG,
        color: "#f1f1f1",
      });

    this.highScoreText = this.add
      .text(width - 16, 58, "Best: 0", {
        fontFamily: "Arial",
        fontSize: HUD_FONT_LG,
        color: "#ffd166",
      })
      .setOrigin(1, 0);

    this.statusText = this.add
      .text(centerX, 86, "", {
        fontFamily: "Arial",
        fontSize: "14px",
        color: "#a4b0be",
      })
      .setOrigin(0.5);

    const instructY = height - 36;
    this.add
      .text(
        centerX,
        instructY,
        "三个或以上相同人物连在一起即可消除，同一个人物的不同照片也可以消除。",
        {
          fontFamily: "Arial",
          fontSize: "14px",
          color: "#ced6e0",
          align: "center",
          wordWrap: { width: width - 32 },
        },
      )
      .setOrigin(0.5);

    this.newHighScoreMark = null;
  }

  // -------------------------------------------------------------------------
  // Game lifecycle
  // -------------------------------------------------------------------------

  private startNewGame(): void {
    this.score = 0;
    this.updateHUD();
    this.clearBoard();
    this.boardLogic.initialize();
    this.renderBoardInitial();
    this.setStatus("");
    this.lockInput(false);
  }

  private clearBoard(): void {
    for (const ts of this.tiles.values()) {
      ts.image.destroy();
      ts.border.destroy();
    }
    this.tiles.clear();
  }

  private renderBoardInitial(): void {
    for (let r = 0; r < this.rows(); r++) {
      for (let c = 0; c < this.cols(); c++) {
        const tile = this.boardLogic.board[r][c];
        if (!tile) continue;
        this.spawnTileAt(r, c, tile, /* animated */ false);
      }
    }
  }

  private spawnTileAt(
    row: number,
    col: number,
    tile: TileData,
    animated: boolean,
    spawnY?: number,
  ): TileSprite {
    const pos = this.cellToWorld({ row, col });
    const key = `${row},${col}`;

    const border = this.add
      .rectangle(pos.x, pos.y, this.tileSize, this.tileSize, TILE_BORDER_COLOR)
      .setOrigin(0.5)
      .setStrokeStyle(2, TILE_BORDER_COLOR);

    const image = this.add.image(pos.x, pos.y, tile.photoId);
    // Square crop, no distortion: width = height = tile size.
    image.setDisplaySize(this.tileSize, this.tileSize);

    if (animated && spawnY !== undefined) {
      image.y = spawnY;
      border.y = spawnY;
    }

    const sprite: TileSprite = { image, border, data: tile, cell: { row, col } };
    this.tiles.set(key, sprite);
    return sprite;
  }

  // -------------------------------------------------------------------------
  // Input
  // -------------------------------------------------------------------------

  private registerInputHandlers(): void {
    this.input.on("pointerdown", this.onPointerDown, this);
    this.input.on("pointermove", this.onPointerMove, this);
    this.input.on("pointerup", this.onPointerUp, this);
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.inputLocked) return;
    const cell = this.worldToCell(pointer.x, pointer.y);
    if (!cell) return;
    this.pointerOrigin = { cell, x: pointer.x, y: pointer.y };
    this.dragging = false;
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.pointerOrigin || this.inputLocked) return;
    const dx = pointer.x - this.pointerOrigin.x;
    const dy = pointer.y - this.pointerOrigin.y;
    if (Math.abs(dx) + Math.abs(dy) > 12) this.dragging = true;
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.inputLocked || !this.pointerOrigin) {
      this.pointerOrigin = null;
      this.dragging = false;
      return;
    }

    const origin = this.pointerOrigin;
    this.pointerOrigin = null;

    if (this.dragging) {
      this.handleSwipe(origin.cell, pointer.x - origin.x, pointer.y - origin.y);
    } else {
      this.handleTap(origin.cell);
    }
    this.dragging = false;
  }

  private handleTap(cell: Cell): void {
    if (!this.selected) {
      this.selectTile(cell);
      return;
    }
    if (this.sameCell(this.selected, cell)) {
      this.deselectTile();
      return;
    }
    if (BoardLogic.isAdjacent(this.selected, cell)) {
      const a = this.selected;
      const b = cell;
      this.deselectTile();
      void this.trySwap(a, b);
      return;
    }
    // Non-adjacent: select the new tile instead.
    this.deselectTile();
    this.selectTile(cell);
  }

  private handleSwipe(origin: Cell, dx: number, dy: number): void {
    this.deselectTile();
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    let target: Cell;
    if (adx > ady) {
      target = { row: origin.row, col: origin.col + Math.sign(dx) };
    } else {
      target = { row: origin.row + Math.sign(dy), col: origin.col };
    }
    if (
      target.row < 0 ||
      target.row >= this.rows() ||
      target.col < 0 ||
      target.col >= this.cols()
    ) {
      return;
    }
    void this.trySwap(origin, target);
  }

  private selectTile(cell: Cell): void {
    this.selected = cell;
    const ts = this.tiles.get(`${cell.row},${cell.col}`);
    if (ts) {
      ts.border.setFillStyle(TILE_SELECTED_COLOR, 0.45);
      ts.border.setStrokeStyle(3, TILE_SELECTED_COLOR);
    }
  }

  private deselectTile(): void {
    if (!this.selected) return;
    const ts = this.tiles.get(`${this.selected.row},${this.selected.col}`);
    if (ts) {
      ts.border.setFillStyle(TILE_BORDER_COLOR);
      ts.border.setStrokeStyle(2, TILE_BORDER_COLOR);
    }
    this.selected = null;
  }

  private sameCell(a: Cell, b: Cell): boolean {
    return a.row === b.row && a.col === b.col;
  }

  // -------------------------------------------------------------------------
  // Swap / chain
  // -------------------------------------------------------------------------

  private async trySwap(a: Cell, b: Cell): Promise<void> {
    if (this.inputLocked) return;
    this.lockInput(true);
    const valid = this.boardLogic.wouldSwapMatch(a, b);
    const tileA = this.tiles.get(`${a.row},${a.col}`);
    const tileB = this.tiles.get(`${b.row},${b.col}`);
    if (!tileA || !tileB) {
      this.lockInput(false);
      return;
    }
    await this.animateSwapVisual(tileA, tileB, a, b);
    if (!valid) {
      // Bounce-back: visually swap the two tiles back into their original cells.
      // tileA and tileB still reference the same sprite bundles (now at b and a
      // respectively after the forward animation above). Pass them swapped
      // with the (b, a) cell pair so the Map and cell tags end up restored.
      await this.animateSwapVisual(tileA, tileB, b, a);
      this.lockInput(false);
      return;
    }
    // Commit swap in BoardLogic.
    this.boardLogic.swap(a, b);
    await this.resolveChainAndAnimate();
    this.lockInput(false);
  }

  /** Animate two tile sprites into each other's positions. No state change. */
  private animateSwapVisual(
    tileA: TileSprite,
    tileB: TileSprite,
    cellA: Cell,
    cellB: Cell,
  ): Promise<void> {
    const posA = this.cellToWorld(cellA);
    const posB = this.cellToWorld(cellB);
    return new Promise<void>((resolve) => {
      this.tweens.add({
        targets: [tileA.image, tileA.border],
        x: posB.x,
        y: posB.y,
        duration: 180,
        ease: "Sine.easeInOut",
      });
      this.tweens.add({
        targets: [tileB.image, tileB.border],
        x: posA.x,
        y: posA.y,
        duration: 180,
        ease: "Sine.easeInOut",
        onComplete: () => {
          this.tiles.set(`${cellA.row},${cellA.col}`, tileB);
          this.tiles.set(`${cellB.row},${cellB.col}`, tileA);
          tileA.cell = cellB;
          tileB.cell = cellA;
          resolve();
        },
      });
    });
  }

  private async resolveChainAndAnimate(): Promise<void> {
    // Loop: match -> fall -> refill -> repeat.
    let chain = 0;
    while (true) {
      const groups = this.boardLogic.findMatches();
      if (groups.length === 0) break;

      chain++;
      const matchedCells = MatchCells(groups);
      const gained = matchedCells.size * 10 * chain;
      this.score += gained;

      // 1. Eliminate matched tiles (scale + fade).
      await this.animateEliminate(matchedCells);

      // 2. Compute collapse and animate fall + refill.
      const result = this.boardLogic.collapse(groups);
      await this.animateCollapse(result);

      // HUD updates after every chain step.
      this.updateHUD();
    }

    // Lock achievements check after all chains resolved.
    this.checkHighScore();
    this.setStatus(chain > 1 ? `Combo x${chain}!` : "");

    // If board has no valid swap left, shuffle.
    if (!this.boardLogic.hasValidSwap()) {
      this.setStatus("No moves — shuffling...");
      await this.delay(420);
      await this.animateShuffle();
      this.boardLogic.shuffle();
      this.clearBoard();
      this.renderBoardInitial();
      this.setStatus("");
    }
  }

  private async animateEliminate(cells: Set<string>): Promise<void> {
    const targets: Phaser.GameObjects.GameObject[] = [];
    for (const key of cells) {
      const ts = this.tiles.get(key);
      if (ts) {
        targets.push(ts.image);
        targets.push(ts.border);
      }
    }
    await new Promise<void>((resolve) => {
      this.tweens.add({
        targets,
        scale: 0,
        alpha: 0,
        duration: 220,
        ease: "Back.easeIn",
        onComplete: () => {
          for (const key of cells) {
            const ts = this.tiles.get(key);
            if (ts) {
              ts.image.destroy();
              ts.border.destroy();
              this.tiles.delete(key);
            }
          }
          resolve();
        },
      });
    });
  }

  private async animateCollapse(result: {
    removedCells: Cell[];
    fallMoves: { from: Cell; to: Cell }[];
    newTiles: { cell: Cell; tile: TileData }[];
  }): Promise<void> {
    const fallDuration = 220;
    const fallPromises: Promise<void>[] = [];
    for (const move of result.fallMoves) {
      const key = `${move.from.row},${move.from.col}`;
      const ts = this.tiles.get(key);
      if (!ts) continue;
      const target = this.cellToWorld(move.to);
      this.tiles.delete(key);
      this.tiles.set(`${move.to.row},${move.to.col}`, ts);
      ts.cell = move.to;
      fallPromises.push(
        new Promise<void>((resolve) => {
          this.tweens.add({
            targets: [ts.image, ts.border],
            x: target.x,
            y: target.y,
            duration: fallDuration,
            ease: "Quad.easeIn",
            onComplete: () => resolve(),
          });
        }),
      );
    }

    // Spawn new tiles above the board then drop them in.
    const spawnAbove = this.boardY - this.tileSize - 8;
    const spawnPromises: Promise<void>[] = [];
    for (const nt of result.newTiles) {
      const pos = this.cellToWorld(nt.cell);
      // Create the sprite at the spawn-above position then tween down.
      const ts = this.spawnTileAt(nt.cell.row, nt.cell.col, nt.tile, true, spawnAbove);
      ts.image.y = spawnAbove;
      ts.border.y = spawnAbove;
      spawnPromises.push(
        new Promise<void>((resolve) => {
          this.tweens.add({
            targets: [ts.image, ts.border],
            x: pos.x,
            y: pos.y,
            duration: fallDuration,
            ease: "Quad.easeIn",
            onComplete: () => resolve(),
          });
        }),
      );
    }

    await Promise.all([...fallPromises, ...spawnPromises]);
  }

  private async animateShuffle(): Promise<void> {
    // Brief fade before the swap, so the shuffle "feels" like a new deal.
    const imgs: Phaser.GameObjects.Image[] = [];
    for (const ts of this.tiles.values()) imgs.push(ts.image);
    await new Promise<void>((resolve) => {
      this.tweens.add({
        targets: imgs,
        alpha: 0,
        scale: 0.6,
        duration: 160,
        ease: "Sine.easeOut",
        onComplete: () => resolve(),
      });
    });
  }

  // -------------------------------------------------------------------------
  // HUD helpers
  // -------------------------------------------------------------------------

  private updateHUD(): void {
    this.scoreText.setText(`Score: ${this.score}`);
    this.highScoreText.setText(`Best: ${this.highScore}`);
  }

  private setStatus(text: string): void {
    this.statusText.setText(text);
  }

  private lockInput(locked: boolean): void {
    this.inputLocked = locked;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => this.time.delayedCall(ms, resolve));
  }

  // -------------------------------------------------------------------------
  // High score (localStorage)
  // -------------------------------------------------------------------------

  private static readonly HIGH_SCORE_KEY = "match3:highScore";

  private loadHighScore(): void {
    try {
      const raw = localStorage.getItem(GameScene.HIGH_SCORE_KEY);
      this.highScore = raw ? Math.max(0, parseInt(raw, 10) || 0) : 0;
    } catch {
      this.highScore = 0;
    }
  }

  private saveHighScore(): void {
    try {
      localStorage.setItem(
        GameScene.HIGH_SCORE_KEY,
        String(this.highScore),
      );
    } catch {
      /* ignore (private mode / quota / disabled storage) */
    }
  }

  private checkHighScore(): void {
    if (this.score <= this.highScore) return;
    this.highScore = this.score;
    this.saveHighScore();
    this.updateHUD();
    if (!this.newHighScoreMark) {
      const { width, height } = this.scale;
      this.newHighScoreMark = this.add
        .text(width / 2, height / 2 - 80, "NEW BEST!", {
          fontFamily: "Arial",
          fontSize: "28px",
          color: "#ffd166",
        })
        .setOrigin(0.5);
      this.tweens.add({
        targets: this.newHighScoreMark,
        alpha: 0,
        y: (this.newHighScoreMark.y ?? 0) - 30,
        duration: 1200,
        delay: 600,
        onComplete: () => {
          if (this.newHighScoreMark) {
            this.newHighScoreMark.destroy();
            this.newHighScoreMark = null;
          }
        },
      });
    }
  }
}

/** Flatten a list of match groups to a set of "row,col" keys. */
function MatchCells(groups: { cells: Cell[] }[]): Set<string> {
  const out = new Set<string>();
  for (const g of groups) {
    for (const cell of g.cells) out.add(`${cell.row},${cell.col}`);
  }
  return out;
}
